import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Button, Switch, FormControlLabel,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { AuthContext } from '../../components/signin/AuthContext';
import { useGrowSetup } from '../../GrowSetupContext';

function getUserId(token) {
  try { return JSON.parse(atob(token.split('.')[1])).sub || 0; }
  catch (_) { return 0; }
}

const GLASS = {
  background: 'rgba(var(--v4-surface-rgb),0.9)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
  borderRadius: '14px',
  backdropFilter: 'blur(20px)',
};

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    background: 'rgba(var(--v4-cyan-rgb),0.04)',
    borderRadius: '10px',
    color: 'var(--v4-text)',
    '& fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.4)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(var(--v4-cyan-rgb),0.6)' },
};

const DIALOG_PAPER = {
  background: 'rgba(var(--v4-surface2-rgb),0.97)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.2)',
  borderRadius: '18px',
  backdropFilter: 'blur(24px)',
};

const MEASURE_OPTS = [
  { value: 'temperatur', label: 'Temperatur (°C)' },
  { value: 'luftfeuchte', label: 'Luftfeuchte (%)' },
  { value: 'co2', label: 'CO₂ (ppm)' },
  { value: 'ec', label: 'EC (mS/cm)' },
  { value: 'ph', label: 'pH-Wert' },
];

const CONDITION_OPTS = [
  { value: 'out_of_range', label: 'Außerhalb des Bereichs' },
  { value: 'above', label: 'Über dem Grenzwert' },
  { value: 'below', label: 'Unter dem Grenzwert' },
];

function SectionLabel({ icon: Icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      {Icon && <Icon sx={{ fontSize: '1rem', color: 'rgba(var(--v4-cyan-rgb),0.6)' }} />}
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(var(--v4-cyan-rgb),0.6)', whiteSpace: 'nowrap' }}>
        {children}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: 'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

function ChannelChip({ ch, onToggle, onDelete }) {
  const isEmail = ch.channel_type === 'email';
  const color = isEmail ? 'var(--v4-cyan)' : 'var(--v4-whatsapp)';
  return (
    <Box sx={{
      ...GLASS,
      p: 1.5,
      display: 'flex', alignItems: 'center', gap: 1,
      opacity: ch.is_active ? 1 : 0.45,
    }}>
      {isEmail
        ? <EmailIcon sx={{ fontSize: '1.1rem', color }} />
        : <PhoneAndroidIcon sx={{ fontSize: '1.1rem', color }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--v4-text)', lineHeight: 1.2 }}>
          {ch.label || (isEmail ? 'E-Mail' : 'WhatsApp')}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(var(--v4-muted-rgb),0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ch.value}
        </Typography>
      </Box>
      <Tooltip title={ch.is_active ? 'Deaktivieren' : 'Aktivieren'}>
        <Switch size="small" checked={Boolean(ch.is_active)} onChange={() => onToggle(ch)}
          sx={{ '& .Mui-checked': { color }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: color } }}
        />
      </Tooltip>
      <Tooltip title="Löschen">
        <IconButton size="small" onClick={() => onDelete(ch.id)} sx={{ color: 'rgba(var(--v4-red-soft-rgb),0.5)', '&:hover': { color: 'var(--v4-red)' } }}>
          <DeleteIcon sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function RuleCard({ rule, channels, onEdit, onDelete, onTest, onToggle }) {
  const measure = MEASURE_OPTS.find(m => m.value === rule.measure)?.label || rule.measure;
  const condition = CONDITION_OPTS.find(c => c.value === rule.condition)?.label || rule.condition;
  const linkedChannels = (rule.channels || channels).filter(c => c.is_active);

  return (
    <Box sx={{ ...GLASS, p: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography sx={{ fontWeight: 800, color: 'var(--v4-text)', fontSize: '0.9rem' }}>{measure}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(var(--v4-muted-rgb),0.5)', mt: 0.3 }}>{condition}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={rule.is_active ? 'Deaktivieren' : 'Aktivieren'}>
            <Switch size="small" checked={Boolean(rule.is_active)} onChange={() => onToggle(rule)}
              sx={{ '& .Mui-checked': { color: 'var(--v4-cyan)' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--v4-cyan)' } }}
            />
          </Tooltip>
          <Tooltip title="Testen">
            <IconButton size="small" onClick={() => onTest(rule.id)} sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)', '&:hover': { color: 'var(--v4-cyan)' } }}>
              <SendIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <IconButton size="small" onClick={() => onEdit(rule)} sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)', '&:hover': { color: 'var(--v4-cyan)' } }}>
              <EditIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Löschen">
            <IconButton size="small" onClick={() => onDelete(rule.id)} sx={{ color: 'rgba(var(--v4-red-soft-rgb),0.4)', '&:hover': { color: 'var(--v4-red)' } }}>
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        {rule.threshold_lower != null && (
          <Chip label={`Min: ${rule.threshold_lower}`} size="small"
            sx={{ background: 'rgba(var(--v4-cyan-rgb),0.08)', border: '1px solid rgba(var(--v4-cyan-rgb),0.15)', color: 'var(--v4-cyan)', fontSize: '0.65rem' }} />
        )}
        {rule.threshold_upper != null && (
          <Chip label={`Max: ${rule.threshold_upper}`} size="small"
            sx={{ background: 'rgba(var(--v4-cyan-rgb),0.08)', border: '1px solid rgba(var(--v4-cyan-rgb),0.15)', color: 'var(--v4-cyan)', fontSize: '0.65rem' }} />
        )}
        <Chip label={`Cooldown ${rule.cooldown_minutes}min`} size="small"
          sx={{ background: 'rgba(80,80,80,0.2)', border: '1px solid rgba(var(--v4-muted-rgb),0.1)', color: 'rgba(var(--v4-muted-rgb),0.5)', fontSize: '0.65rem' }} />
      </Stack>

      {linkedChannels.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {linkedChannels.slice(0, 3).map(ch => (
            <Chip
              key={ch.id}
              icon={ch.channel_type === 'email' ? <EmailIcon sx={{ fontSize: '0.8rem !important' }} /> : <PhoneAndroidIcon sx={{ fontSize: '0.8rem !important' }} />}
              label={ch.label || ch.value}
              size="small"
              sx={{ background: 'rgba(var(--v4-shadow-rgb),calc(0.2*var(--v4-shadow-k)))', border: '1px solid rgba(var(--v4-muted-rgb),0.1)', color: 'rgba(var(--v4-muted-rgb),0.6)', fontSize: '0.62rem', maxWidth: 120 }}
            />
          ))}
          {linkedChannels.length > 3 && (
            <Chip label={`+${linkedChannels.length - 3}`} size="small"
              sx={{ background: 'rgba(var(--v4-shadow-rgb),calc(0.2*var(--v4-shadow-k)))', color: 'rgba(var(--v4-muted-rgb),0.4)', fontSize: '0.62rem' }} />
          )}
        </Stack>
      )}

      {rule.last_triggered_at && (
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(var(--v4-muted-rgb),0.3)', mt: 1 }}>
          Zuletzt: {new Date(rule.last_triggered_at).toLocaleString('de-DE')}
        </Typography>
      )}
    </Box>
  );
}

const EMPTY_RULE = { measure: 'temperatur', condition: 'out_of_range', threshold_upper: null, threshold_lower: null, cooldown_minutes: 60, notify_channel_ids: null };
const EMPTY_CHANNEL = { channel_type: 'email', value: '', label: '' };

export default function V4AlertConfigPage() {
  const { token } = useContext(AuthContext);
  const { grow_setup } = useGrowSetup();
  const userId = getUserId(token);

  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [channelDlgOpen, setChannelDlgOpen] = useState(false);
  const [channelDraft, setChannelDraft] = useState(EMPTY_CHANNEL);

  const [ruleDlgOpen, setRuleDlgOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(EMPTY_RULE);
  const [editingRuleId, setEditingRuleId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const historyInterval = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!grow_setup) return;
    setLoading(true);
    try {
      const [chRes, ruleRes, histRes] = await Promise.all([
        axios.get('/api/v4/notification-channels', { headers: authHeaders, params: { grow_setup_id: grow_setup } }),
        axios.get('/api/v4/alert-rules', { headers: authHeaders, params: { grow_setup_id: grow_setup } }),
        axios.get('/api/v4/alert-history', { headers: authHeaders, params: { grow_setup_id: grow_setup, limit: 20 } }),
      ]);
      setChannels(chRes.data.channels || []);
      setRules(ruleRes.data.rules || []);
      setHistory(histRes.data.history || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [grow_setup, token]);

  const loadHistory = useCallback(async () => {
    if (!grow_setup) return;
    try {
      const res = await axios.get('/api/v4/alert-history', { headers: authHeaders, params: { grow_setup_id: grow_setup, limit: 20 } });
      setHistory(res.data.history || []);
    } catch (_) {}
  }, [grow_setup, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    historyInterval.current = setInterval(loadHistory, 30000);
    return () => clearInterval(historyInterval.current);
  }, [loadHistory]);

  // ── Channel handlers ──────────────────────────────────────────────────────

  const handleSaveChannel = async () => {
    if (!channelDraft.value.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/v4/notification-channels', {
        grow_setup_id: grow_setup,
        user_id: userId,
        channel_type: channelDraft.channel_type,
        value: channelDraft.value.trim(),
        label: channelDraft.label.trim() || null,
      }, { headers: authHeaders });
      setChannelDlgOpen(false);
      setChannelDraft(EMPTY_CHANNEL);
      await load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleToggleChannel = async (ch) => {
    try {
      await axios.put(`/api/v4/notification-channels/${ch.id}`, { is_active: !ch.is_active }, { headers: authHeaders });
      await load();
    } catch (e) { alert(e.message); }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('Kanal löschen?')) return;
    try {
      await axios.delete(`/api/v4/notification-channels/${id}`, { headers: authHeaders });
      await load();
    } catch (e) { alert(e.message); }
  };

  // ── Rule handlers ─────────────────────────────────────────────────────────

  const openNewRule = () => {
    setEditingRuleId(null);
    setRuleDraft({ ...EMPTY_RULE });
    setRuleDlgOpen(true);
  };

  const openEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setRuleDraft({
      measure: rule.measure,
      condition: rule.condition,
      threshold_upper: rule.threshold_upper,
      threshold_lower: rule.threshold_lower,
      cooldown_minutes: rule.cooldown_minutes,
      notify_channel_ids: rule.notify_channel_ids,
    });
    setRuleDlgOpen(true);
  };

  const handleSaveRule = async () => {
    setSaving(true);
    try {
      const payload = {
        grow_setup_id: grow_setup,
        ...ruleDraft,
        threshold_upper: ruleDraft.threshold_upper !== '' ? Number(ruleDraft.threshold_upper) : null,
        threshold_lower: ruleDraft.threshold_lower !== '' ? Number(ruleDraft.threshold_lower) : null,
        cooldown_minutes: Number(ruleDraft.cooldown_minutes) || 60,
      };
      if (editingRuleId) {
        await axios.put(`/api/v4/alert-rules/${editingRuleId}`, payload, { headers: authHeaders });
      } else {
        await axios.post('/api/v4/alert-rules', payload, { headers: authHeaders });
      }
      setRuleDlgOpen(false);
      await load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Alert-Regel löschen?')) return;
    try {
      await axios.delete(`/api/v4/alert-rules/${id}`, { headers: authHeaders });
      await load();
    } catch (e) { alert(e.message); }
  };

  const handleToggleRule = async (rule) => {
    try {
      await axios.put(`/api/v4/alert-rules/${rule.id}`, { is_active: !rule.is_active }, { headers: authHeaders });
      await load();
    } catch (e) { alert(e.message); }
  };

  const handleTestRule = async (id) => {
    setTestResult(null);
    try {
      const res = await axios.post(`/api/v4/alert-rules/${id}/test`, {}, { headers: authHeaders });
      setTestResult(res.data.results || []);
      await loadHistory();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: 'var(--v4-cyan)' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Channels ── */}
      <SectionLabel icon={PhoneAndroidIcon}>Benachrichtigungskanäle</SectionLabel>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="stretch">
          {channels.map(ch => (
            <Box key={ch.id} sx={{ minWidth: 220, maxWidth: 320 }}>
              <ChannelChip ch={ch} onToggle={handleToggleChannel} onDelete={handleDeleteChannel} />
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />} variant="outlined" size="small"
            onClick={() => { setChannelDraft(EMPTY_CHANNEL); setChannelDlgOpen(true); }}
            sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.25)', color: 'var(--v4-cyan)', borderRadius: '14px', px: 2, height: 68, minWidth: 160, flexDirection: 'column', gap: 0.3 }}
          >
            Kanal hinzufügen
          </Button>
        </Stack>
        {channels.length === 0 && (
          <Typography sx={{ color: 'rgba(var(--v4-muted-rgb),0.3)', fontSize: '0.82rem', mt: 1 }}>
            Noch keine Kanäle. Füge E-Mail oder WhatsApp hinzu.
          </Typography>
        )}
      </Box>

      {/* ── Alert Rules ── */}
      <SectionLabel icon={NotificationsActiveIcon}>Alert-Regeln</SectionLabel>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start">
          {rules.map(r => (
            <Box key={r.id} sx={{ width: { xs: '100%', sm: 280 } }}>
              <RuleCard
                rule={r}
                channels={channels}
                onEdit={openEditRule}
                onDelete={handleDeleteRule}
                onTest={handleTestRule}
                onToggle={handleToggleRule}
              />
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />} variant="outlined" size="small"
            onClick={openNewRule}
            sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.25)', color: 'var(--v4-cyan)', borderRadius: '14px', px: 2, height: 80, minWidth: 160, flexDirection: 'column', gap: 0.3 }}
          >
            Regel hinzufügen
          </Button>
        </Stack>
      </Box>

      {/* Test result snack */}
      {testResult && (
        <Box sx={{ ...GLASS, p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--v4-text)', fontSize: '0.85rem' }}>Test-Ergebnis</Typography>
            <IconButton size="small" onClick={() => setTestResult(null)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>
              <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Stack>
          <Stack spacing={0.5}>
            {testResult.map((r, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '0.65rem', color: r.success ? 'var(--v4-green)' : 'var(--v4-red)' }}>
                  {r.success ? '✓' : '✗'}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>
                  {r.type} → {r.channel} {r.error ? `(${r.error})` : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── History ── */}
      <SectionLabel icon={HistoryIcon}>Benachrichtigungs-Verlauf</SectionLabel>
      <Box sx={{ ...GLASS, overflow: 'hidden' }}>
        {history.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(var(--v4-muted-rgb),0.3)', fontSize: '0.82rem' }}>Noch keine Benachrichtigungen gesendet.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: '1px solid rgba(var(--v4-cyan-rgb),0.1)' }}>
                  {['Zeitpunkt', 'Kanal', 'Empfänger', 'Nachricht', 'Status'].map(h => (
                    <Box component="th" key={h} sx={{ p: '8px 12px', textAlign: 'left', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(var(--v4-cyan-rgb),0.5)' }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {history.map(h => (
                  <Box component="tr" key={h.id} sx={{ borderBottom: '1px solid rgba(var(--v4-cyan-rgb),0.04)', '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.03)' } }}>
                    <Box component="td" sx={{ p: '7px 12px', fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.5)', whiteSpace: 'nowrap' }}>
                      {new Date(h.triggered_at).toLocaleString('de-DE')}
                    </Box>
                    <Box component="td" sx={{ p: '7px 12px' }}>
                      {h.channel_type === 'email'
                        ? <EmailIcon sx={{ fontSize: '0.9rem', color: 'var(--v4-cyan)', verticalAlign: 'middle' }} />
                        : <PhoneAndroidIcon sx={{ fontSize: '0.9rem', color: 'var(--v4-whatsapp)', verticalAlign: 'middle' }} />}
                    </Box>
                    <Box component="td" sx={{ p: '7px 12px', fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.6)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.sent_to}
                    </Box>
                    <Box component="td" sx={{ p: '7px 12px', fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.5)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.message.split('\n')[0]}
                    </Box>
                    <Box component="td" sx={{ p: '7px 12px', fontSize: '0.75rem', color: h.success ? 'var(--v4-green)' : 'var(--v4-red)', fontWeight: 700 }}>
                      {h.success ? '✓' : '✗'}
                      {!h.success && h.error_msg && (
                        <Typography component="span" sx={{ fontSize: '0.6rem', color: 'rgba(var(--v4-red-soft-rgb),0.6)', ml: 0.5 }}>
                          {h.error_msg.slice(0, 40)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Add Channel Dialog ── */}
      <Dialog open={channelDlgOpen} onClose={() => setChannelDlgOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: DIALOG_PAPER }}>
        <DialogTitle sx={{ color: 'var(--v4-text)', fontWeight: 700 }}>Benachrichtigungskanal hinzufügen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Typ</InputLabel>
              <Select label="Typ" value={channelDraft.channel_type} onChange={e => setChannelDraft(d => ({ ...d, channel_type: e.target.value }))}
                sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}>
                <MenuItem value="email"><Stack direction="row" spacing={1} alignItems="center"><EmailIcon sx={{ fontSize: '1rem', color: 'var(--v4-cyan)' }} /><span>E-Mail</span></Stack></MenuItem>
                <MenuItem value="whatsapp"><Stack direction="row" spacing={1} alignItems="center"><PhoneAndroidIcon sx={{ fontSize: '1rem', color: 'var(--v4-whatsapp)' }} /><span>WhatsApp</span></Stack></MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth size="small" label={channelDraft.channel_type === 'email' ? 'E-Mail-Adresse' : 'Rufnummer (z.B. +491701234567)'}
              value={channelDraft.value} onChange={e => setChannelDraft(d => ({ ...d, value: e.target.value }))} sx={INPUT_SX} />
            <TextField fullWidth size="small" label="Bezeichnung (optional)" value={channelDraft.label}
              onChange={e => setChannelDraft(d => ({ ...d, label: e.target.value }))} sx={INPUT_SX} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChannelDlgOpen(false)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>Abbrechen</Button>
          <Button onClick={handleSaveChannel} disabled={saving} variant="contained"
            sx={{ background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))', borderRadius: '10px' }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Rule Dialog ── */}
      <Dialog open={ruleDlgOpen} onClose={() => setRuleDlgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER }}>
        <DialogTitle sx={{ color: 'var(--v4-text)', fontWeight: 700 }}>{editingRuleId ? 'Alert-Regel bearbeiten' : 'Alert-Regel hinzufügen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Messwert</InputLabel>
              <Select label="Messwert" value={ruleDraft.measure} onChange={e => setRuleDraft(d => ({ ...d, measure: e.target.value }))}
                sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}>
                {MEASURE_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Bedingung</InputLabel>
              <Select label="Bedingung" value={ruleDraft.condition} onChange={e => setRuleDraft(d => ({ ...d, condition: e.target.value }))}
                sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}>
                {CONDITION_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              {(ruleDraft.condition === 'out_of_range' || ruleDraft.condition === 'below') && (
                <TextField fullWidth size="small" type="number" label="Untere Grenze"
                  value={ruleDraft.threshold_lower ?? ''} onChange={e => setRuleDraft(d => ({ ...d, threshold_lower: e.target.value }))} sx={INPUT_SX} />
              )}
              {(ruleDraft.condition === 'out_of_range' || ruleDraft.condition === 'above') && (
                <TextField fullWidth size="small" type="number" label="Obere Grenze"
                  value={ruleDraft.threshold_upper ?? ''} onChange={e => setRuleDraft(d => ({ ...d, threshold_upper: e.target.value }))} sx={INPUT_SX} />
              )}
            </Stack>
            <TextField fullWidth size="small" type="number" label="Cooldown (Minuten)"
              value={ruleDraft.cooldown_minutes} onChange={e => setRuleDraft(d => ({ ...d, cooldown_minutes: e.target.value }))}
              helperText="Mindestabstand zwischen zwei Benachrichtigungen"
              sx={{ ...INPUT_SX, '& .MuiFormHelperText-root': { color: 'rgba(var(--v4-muted-rgb),0.4)' } }} />
            {channels.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(var(--v4-cyan-rgb),0.6)', mb: 0.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Kanäle (leer = alle aktiven)
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.8}>
                  {channels.map(ch => {
                    const selected = Array.isArray(ruleDraft.notify_channel_ids) && ruleDraft.notify_channel_ids.includes(ch.id);
                    return (
                      <Chip key={ch.id}
                        icon={ch.channel_type === 'email' ? <EmailIcon /> : <PhoneAndroidIcon />}
                        label={ch.label || ch.value}
                        onClick={() => {
                          const curr = Array.isArray(ruleDraft.notify_channel_ids) ? ruleDraft.notify_channel_ids : [];
                          const next = selected ? curr.filter(id => id !== ch.id) : [...curr, ch.id];
                          setRuleDraft(d => ({ ...d, notify_channel_ids: next.length ? next : null }));
                        }}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer', fontSize: '0.7rem',
                          background: selected ? 'rgba(var(--v4-cyan-rgb),0.15)' : 'transparent',
                          border: `1px solid ${selected ? 'rgba(var(--v4-cyan-rgb),0.5)' : 'rgba(var(--v4-muted-rgb),0.15)'}`,
                          color: selected ? 'var(--v4-cyan)' : 'rgba(var(--v4-muted-rgb),0.5)',
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRuleDlgOpen(false)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>Abbrechen</Button>
          <Button onClick={handleSaveRule} disabled={saving} variant="contained"
            sx={{ background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))', borderRadius: '10px' }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
